/**
 * J 域净资产**接口契约** —— `GET /api/networth`（首页与净资产）
 *
 * 依据：J 域 PRD **§4.1**（`composeNetWorth` 唯一权威）· **§4.7**（首页 Hero 必须改用 `breakdown`）
 * · `DR-012`（P0 即全量口径）· `DR-026`（负债 6 类）· 规格 **§12** · 架构 **§5.4** · **§6.1**。
 *
 * ## 🔴 为什么本文件**不新增任何口径**，只做"接口形状"
 *
 * 净资产的全部计算都在共享层纯函数 `nucleus/networth.ts` 的 `composeNetWorth()` 里
 * （资产 10 类 / 负债 6 类 / `internal` 净额 / `insurance_sav` 例外 / 多币种折算）。
 * 本文件**只**在它的输出上加两个**传输层**字段 —— 因为"哪些来源还没有接口"这件事
 * 是**服务端才知道的事实**，不是口径：
 *
 * | 字段 | 为什么需要 |
 * |---|---|
 * | `notReady` | 未落地的资产类（持仓 / 重大资产 / 手动资产 / 手动负债 / 分期本金）目前**无取数来源**，`composeNetWorth` 会算出 `0` —— 而 `0` 与"这项确实是 0"**无法区分**。⇒ 显式列出，UI 才能标注「未就绪」而不是显示 `¥0`。 |
 * | `partial` | 有 `notReady` 项时，`totalAssets` / `netWorth` 都只是**下界**。UI 必须标注，否则用户以为家底就这么点。 |
 *
 * 🔴 **两者都是"诚实性"字段**（J 域 PRD §4.5「曲线的诚实性」同源）：宁可显示得少，
 * 也不让"接口没写"伪装成"金额为 0"。
 */

import { z } from 'zod';
import type { NetWorthResult } from '../nucleus/networth.js';

/**
 * 一类**暂无取数来源**的净资产构成项。
 *
 * ⚠️ **不是"永远不做"** —— 是"表已建好、接口还没写"。`table` 字段让接手人一眼看出
 * 是「缺接口」还是「连表都没有」，避免重复建表。
 */
export interface NetWorthNotReady {
  /** 与 `ASSET_BUCKETS` / `LIABILITY_BUCKETS` 的 `key` 对齐（端侧据此标记对应桶） */
  bucketKey: string;
  /** 展示名（= 分桶定义里的 `label`，服务端下发，端侧不重写） */
  label: string;
  /** 缺什么（**面向用户**，可直接展示） */
  missing: string;
  /** 已建好的数据表名；`null` = 尚未有载体 */
  table: string | null;
}

/**
 * `GET /api/networth` 的响应体。
 *
 * 🔴 **字段与 `NetWorthResult` 完全一致 + 两个诚实性字段** —— 端侧**不得**自己加总、
 * 自己算比例、自己把"未就绪"当成 0（J 域 PRD §4.7：首页净资产卡必须改用 `breakdown`，
 * v1 的 legacy 派生字段正是双口径漂移的起点）。
 */
export interface NetWorthResponse extends NetWorthResult {
  /** 资产/负债两侧**结构完整**（10 + 6 类全在）但其中若干类**暂无数据来源** */
  notReady: readonly NetWorthNotReady[];
  /** `= notReady.length > 0`；为真时 `totalAssets` / `totalLiabilities` / `netWorth` **均为下界** */
  partial: boolean;
}

// ─────────────────────── J2 快照与曲线（`DR-012` · J 域 PRD §4.2 / §4.3） ───────────────────────

/**
 * 快照的**口径范围**。
 *
 * 🔴 这一项的存在理由与 `NetWorthNotReady` 完全同源：**`0` 与"这类没有历史取值"必须能区分**。
 * 回填点算不出第 7/8/9/10 类（保单现金价值 / 手动资产 / 重大资产 / 持仓 —— 它们**没有历史时点值**），
 * 如果只写一个 `0`，消费方会以为"那天没有保单"，而事实是"那一天算不出来"。
 */
export const SnapshotScope = {
  /** 全量口径（10 + 6 类）—— 每日 cron 的 `live` 快照 */
  Full: 'full',
  /**
   * **仅账户层** —— `backfill`（由真实腿数据回填）。
   * 第 7/8/9/10 类**无历史取值**，故其分桶恒为 0 且**不代表 0**（见上）。
   */
  AccountsOnly: 'accountsOnly',
} as const;
export type SnapshotScope = (typeof SnapshotScope)[keyof typeof SnapshotScope];

/** 快照里的一个分桶（**只留呈现必需的三项** —— 快照是历史事实，不需要 `lines` 明细） */
export interface SnapshotBucketLine {
  key: string;
  label: string;
  baseCents: number;
}

/**
 * `net_worth_snapshots.breakdown` 的形状（**带版本号**）。
 *
 * ⚠️ schema 里该列是 `text(mode: 'json')` 且**未打 `$type`** ⇒ 形状由本契约定义。
 * 带 `v` 是为了将来加字段时能安全演进（读旧行不会误判）。
 *
 * 🔴 **小程序 M3 必须消费它**（`DR-012`：小程序净资产不得自算），故字段名一旦发布不可随意改。
 */
export interface SnapshotBreakdown {
  v: 1;
  scope: SnapshotScope;
  baseCurrency: string;
  assets: readonly SnapshotBucketLine[];
  liabilities: readonly SnapshotBucketLine[];
  totalAssets: number;
  totalLiabilities: number;
  /**
   * 🔴 **该点是否用到了「报价日早于快照日」的汇率**（`AC-J2.13`「标记该点为近似」的**唯一落点**）。
   *
   * `true` ⇒ 该点的折算值是**近似**：非交易日（周末 / 节假日）没有当日报价，
   * 按 `R1`（库里**只存真实报价日**）**向前回退**到最近可得的那一天。
   * 判定由**汇率域**做（`RatesService.ratesAsOf()` 的 `allQuotedOnDay`）——
   * J 域**不去猜"这天是不是周末"**（那是日历知识，不属于它）。
   *
   * ⚠️ 与「**缺汇率**」是两件事：缺汇率 ⇒ 该项**不计入**合计并产出 `fxWarnings`
   * （`AC-J1.24`，禁 1:1 冒充）；近似 ⇒ 折算**照常发生**，只是精度差一点。
   * ⚠️ **单币种账本恒为 `false`**（没有折算就没有近似）。
   * ⚠️ 本字段**后加**：`readBreakdown()` 对旧行回填 `false` —— 旧行（本批之前写入）
   * 用的是"最新汇率"，其近似性**无法从行内判断**（见 changelog 的诚实登记）。
   */
  fxApproximated: boolean;
}

/** 曲线上的一个点 = 一行真实快照（🔴 **绝不插值、不平推**） */
export interface NetWorthSnapshotView {
  /** `YYYY-MM-DD`（账本本地日历日） */
  date: string;
  netWorth: number;
  baseCurrency: string;
  /** `live` = 每日 cron；`backfill` = 由真实腿数据回填（见 `SnapshotBreakdown.scope`） */
  origin: 'live' | 'backfill';
  breakdown: SnapshotBreakdown;
}

/** 曲线时间范围三档（J 域 PRD §4.3：12 月 / 3 年 / 全部） */
export const CurveRange = {
  M12: '12m',
  M36: '36m',
  All: 'all',
} as const;
export type CurveRange = (typeof CurveRange)[keyof typeof CurveRange];

/**
 * 三档的**合法取值元组** —— 🔴 由 {@link CurveRange} **派生**，不是另抄一份。
 *
 * 理由：`z.enum` 需要一个字面量元组，若手写一份 `['12m','36m','all']`，
 * 就出现了"档位定义两处"—— 加第四档时必然漏掉一处（与铁律 10 同型的"第二份真相"）。
 */
const CURVE_RANGE_VALUES = [CurveRange.M12, CurveRange.M36, CurveRange.All] as const;

/**
 * `GET /api/networth/curve` 的查询契约。
 *
 * 🔴 **为什么 `range` 是枚举而不是"传月份数"**：三档是**产品定的**（`§4.3`），
 * 不是可自由伸缩的参数 —— 传 `months=7` 会立刻产生"第 4 档"，而档位数一旦能被调用方扩展，
 * 端侧档位控件与服务端取数窗口就会各按一版走。⇒ 契约定死三值，越界即 **400**。
 */
export const CurveQuerySchema = z.object({
  range: z.enum(CURVE_RANGE_VALUES).default(CurveRange.M12),
});
export type CurveQuery = z.infer<typeof CurveQuerySchema>;

/**
 * `GET /api/networth/curve` 的响应体。
 *
 * 🔴 **只用真实快照**：`points` 里**只有库里真实存在的行** —— 没有"月"就不返回该月，
 * 不留空点、不连假线、不外推未来（`DR-012` 拍板原话「过去的不能预测将来的曲线」）。
 */
export interface NetWorthCurveResponse {
  baseCurrency: string;
  range: CurveRange;
  /** 该档位下的真实快照点（**升序**） */
  points: readonly NetWorthSnapshotView[];
  /**
   * `points.length >= SnapshotPolicy.MinPointsForCurve`。
   * 为 `false` 时前端**必须**显示空态「数据积累中」，**不得**画任何线。
   */
  enoughForCurve: boolean;
}
